import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import toaster from 'react-hot-toast';
import {
  type CancelDeviceFirmwareUpdateResponse,
  cancelDeviceFirmwareUpdate,
} from 'api/admin/firmware/firmware';

type CancelDeviceFirmwareUpdateVariables = {
  deviceId: number;
};

export const CANCEL_FIRMWARE_UPDATE_ERROR_MESSAGE = 'Failed to cancel the firmware update';

export const useCancelDeviceFirmwareUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError<{ error: string }>, CancelDeviceFirmwareUpdateVariables>({
    mutationFn: async ({ deviceId }: CancelDeviceFirmwareUpdateVariables) => {
      const { data } = (await cancelDeviceFirmwareUpdate(
        deviceId,
      )) as AxiosResponse<CancelDeviceFirmwareUpdateResponse>;

      if (!data?.success) {
        throw new Error(CANCEL_FIRMWARE_UPDATE_ERROR_MESSAGE);
      }

      return data;
    },
    onSuccess: async (_data, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['washboardDeviceDetails', deviceId] });
      toaster('Firmware update cancelled', {
        icon: '✅',
      });
    },
    onError: (error) => {
      Sentry.captureException(error);

      toaster(
        error?.response?.data?.error ?? error?.message ?? CANCEL_FIRMWARE_UPDATE_ERROR_MESSAGE,
        {
          icon: '❌',
        },
      );
    },
  });
};
